<script lang='ts'>
import { reactive, defineComponent, computed } from '@vue/composition-api';
import { useNotificationSettingsStore, SetConnectedToSlack, SlackDisconnect } from '@/store/global/notification-settings';
import { snackbar, oauthProviders } from '@/_core/app-components/use';

const SlackConnector = defineComponent({
    name: 'SlackConnector',

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props, ctx) {
        const store = useNotificationSettingsStore();

        const state = reactive({
            loading: false,
            dialogOpen: false,
        });

        const storeProps = computed(() => ({
            slackIsConnected: store.state.slackIsConnected,
        }));

        const onOpenSlackOauthWindow = async () => {
            if (storeProps.value.slackIsConnected) {
                return;
            }

            openDialog();
        };

        const openDialog = () => {
            state.loading = true;
            state.dialogOpen = true;
        };

        const closeDialog = () => {
            state.loading = false;
            state.dialogOpen = false;
        };

        const onDisconnectSlack = () => {
            store.dispatch(new SlackDisconnect());
        };

        const onOauthSuccess = (message: string) => {
            store.dispatch(new SetConnectedToSlack(true));
            snackbar.showSuccess(message);
            closeDialog();
        };

        const onOauthError = (message: string) => {
            snackbar.showError(message);
            closeDialog();
        };

        const oauthProvider = oauthProviders.Slack;

        return {
            state,
            storeProps,
            closeDialog,
            onOpenSlackOauthWindow,
            onDisconnectSlack,
            onOauthSuccess,
            onOauthError,
            oauthProvider,
        };
    },
});

export default SlackConnector;
</script>

<template>
  <div class="d-flex align-center py-2 px-3 primary50">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="20.4453125 20.4453125 34.109375 34.109375"
    >
      <path fill="#e01e5a" d="M27.609375 42c0 1.972656-1.609375 3.582031-3.582031 3.582031-1.972656 0-3.582032-1.609375-3.582032-3.582031 0-1.972656 1.609376-3.582031 3.582032-3.582031h3.582031zm1.808594 0c0-1.972656 1.609375-3.582031 3.582031-3.582031 1.972656 0 3.582031 1.609375 3.582031 3.582031v8.972656c0 1.972656-1.609375 3.582032-3.582031 3.582032-1.972656 0-3.582031-1.609376-3.582031-3.582032zm0 0" />
      <path fill="#36c5f0" d="M33 27.609375c-1.972656 0-3.582031-1.609375-3.582031-3.582031 0-1.972656 1.609375-3.582032 3.582031-3.582032 1.972656 0 3.582031 1.609376 3.582031 3.582032v3.582031zm0 1.808594c1.972656 0 3.582031 1.609375 3.582031 3.582031 0 1.972656-1.609375 3.582031-3.582031 3.582031h-8.972656c-1.972656 0-3.582032-1.609375-3.582032-3.582031 0-1.972656 1.609376-3.582031 3.582032-3.582031zm0 0" />
      <path fill="#2eb67d" d="M47.390625 33c0-1.972656 1.609375-3.582031 3.582031-3.582031 1.972656 0 3.582032 1.609375 3.582032 3.582031 0 1.972656-1.609376 3.582031-3.582032 3.582031h-3.582031zm-1.808594 0c0 1.972656-1.609375 3.582031-3.582031 3.582031-1.972656 0-3.582031-1.609375-3.582031-3.582031v-8.972656c0-1.972656 1.609375-3.582032 3.582031-3.582032 1.972656 0 3.582031 1.609376 3.582031 3.582032zm0 0" />
      <path fill="#ecb22e" d="M42 47.390625c1.972656 0 3.582031 1.609375 3.582031 3.582031 0 1.972656-1.609375 3.582032-3.582031 3.582032-1.972656 0-3.582031-1.609376-3.582031-3.582032v-3.582031zm0-1.808594c-1.972656 0-3.582031-1.609375-3.582031-3.582031 0-1.972656 1.609375-3.582031 3.582031-3.582031h8.972656c1.972656 0 3.582032 1.609375 3.582032 3.582031 0 1.972656-1.609376 3.582031-3.582032 3.582031zm0 0" />
    </svg>

    <span v-if="storeProps.slackIsConnected" class="ml-6">You have successfully connected Slack to StoriesOnBoard.</span>
    <span v-if="!storeProps.slackIsConnected" class="ml-6">To start using Slack for personal notifications, you should connect it.</span>
    
    <v-btn
      v-if="!storeProps.slackIsConnected"
      :loading="state.loading"
      small
      dark
      class="ml-auto primary300"
      @click.stop="onOpenSlackOauthWindow"
    >
      Connect Slack
    </v-btn>
    
    <v-btn
      v-if="storeProps.slackIsConnected"
      small
      class="ml-auto primary800"
      dark
      @click.stop="onDisconnectSlack"
    >
      Disconnect Slack
    </v-btn>
    <AppOauthDialog 
      v-if="state.dialogOpen"
      :oauth-provider="oauthProvider" 
      :is-open="state.dialogOpen" 
      @close="closeDialog"
      @success="onOauthSuccess"
      @error="onOauthError"
    />
  </div>
</template>

<style lang='scss' scoped></style>